import { Injectable } from "@angular/core";

export type MonacoModule = typeof import("monaco-editor/esm/vs/editor/editor.api.js");
type MonacoJsonDefaults = typeof import("monaco-editor").json.jsonDefaults;

@Injectable({providedIn: "root"})
export class MonacoEditorLoaderService {

    private monacoPromise: Promise<MonacoModule>;
    private languagePromises = new Map<string, Promise<void>>();
    private workersConfigured = false;

    async load(language = "plaintext"): Promise<MonacoModule> {
        this.configureWorkers();
        // Der Outline-Dienst muss registriert sein, bevor der erste Editor die Monaco-Dienste initialisiert.
        if (this.monacoPromise == null) {
            const promise = Promise.all([
                import("monaco-editor/esm/vs/editor/editor.api.js"),
                import("monaco-editor/esm/vs/editor/contrib/documentSymbols/browser/outlineModel.js")
            ]).then(([monaco]) => monaco);
            // Ein fehlgeschlagenes Nachladen, etwa während eines Dienstneustarts, darf spätere Editoren nicht dauerhaft blockieren
            promise.catch(() => {
                if (this.monacoPromise == promise) {
                    this.monacoPromise = null;
                }
            });
            this.monacoPromise = promise;
        }
        await Promise.all([this.monacoPromise, this.loadLanguage(language)]);
        return this.monacoPromise;
    }

    normalizeLanguage(language: string): string {
        switch (language?.toLowerCase()) {
            case "c#":
            case "cs":
                return "csharp";
            default:
                return language?.toLowerCase() || "plaintext";
        }
    }

    private async loadLanguage(language: string): Promise<void> {
        let normalizedLanguage = this.normalizeLanguage(language);
        let promise = this.languagePromises.get(normalizedLanguage);
        if (promise == null) {
            switch (normalizedLanguage) {
                case "csharp":
                    promise = this.loadCSharp();
                    break;
                case "json":
                    promise = this.loadJson();
                    break;
                case "html":
                    promise = this.loadHtml();
                    break;
                case "vbscript":
                    promise = this.loadVbScript();
                    break;
                case "javascript":
                    promise = this.loadJavaScript();
                    break;
                case "xml":
                    promise = this.loadXml();
                    break;
                case "sql":
                    promise = this.loadSql();
                    break;
                default:
                    promise = Promise.resolve();
                    break;
            }
            const languagePromise = promise;
            this.languagePromises.set(normalizedLanguage, languagePromise);
            languagePromise.catch(() => {
                if (this.languagePromises.get(normalizedLanguage) == languagePromise) {
                    this.languagePromises.delete(normalizedLanguage);
                }
            });
        }
        await promise;
    }

    private async loadCSharp(): Promise<void> {
        await import("monaco-editor/esm/vs/basic-languages/csharp/csharp.contribution.js");
    }

    private async loadJson(): Promise<void> {
        let jsonModule = await import("monaco-editor/esm/vs/language/json/monaco.contribution.js");
        let jsonDefaults = (jsonModule as unknown as {jsonDefaults: MonacoJsonDefaults}).jsonDefaults;
        jsonDefaults.setDiagnosticsOptions({...jsonDefaults.diagnosticsOptions, comments: "ignore"});
        // Ohne Schema schlägt der JSON-Dienst nur unpassende Namen aus dem Dokument vor; gezielte Vorschläge liefert der jeweilige Editor
        jsonDefaults.setModeConfiguration({...jsonDefaults.modeConfiguration, completionItems: false});
    }

    private async loadJavaScript(): Promise<void> {
        await import("monaco-editor/esm/vs/basic-languages/javascript/javascript.contribution.js");
    }

    private async loadHtml(): Promise<void> {
        await import("monaco-editor/esm/vs/basic-languages/html/html.contribution.js");
    }

    private async loadXml(): Promise<void> {
        await import("monaco-editor/esm/vs/basic-languages/xml/xml.contribution.js");
    }

    private async loadSql(): Promise<void> {
        await import("monaco-editor/esm/vs/basic-languages/sql/sql.contribution.js");
    }

    private async loadVbScript(): Promise<void> {
        let monaco = await this.monacoPromise;
        monaco.languages.register({id: "vbscript"});
        monaco.languages.setMonarchTokensProvider("vbscript", {
            ignoreCase: true,
            keywords: [
                "If", "Then", "Else", "ElseIf", "End", "While", "Wend", "For", "To", "Each", "In", "Case", "Select",
                "Return", "Continue", "Do", "Until", "Loop", "Next", "With", "Exit", "Function", "Property", "Type",
                "Enum", "Sub", "IIf", "Class", "Dim", "Call", "Const", "Redim", "Set", "Let", "Get", "New", "Randomize",
                "Option", "Explicit", "Preserve", "Erase", "Execute", "ExecuteGlobal", "Private", "Public", "Default",
                "Mod", "And", "Not", "Or", "Xor", "As", "Eqv", "Imp", "Is"
            ],
            constants: ["Empty", "False", "Nothing", "Null", "True", "Me"],
            builtins: [
                "Array", "Asc", "CBool", "CByte", "CCur", "CDate", "CDbl", "Chr", "CInt", "CLng", "CreateObject",
                "CSng", "CStr", "Date", "DateAdd", "DateDiff", "DatePart", "DateSerial", "DateValue", "Day", "Eval",
                "Execute", "Filter", "FormatCurrency", "FormatDateTime", "FormatNumber", "GetObject", "Hex", "Hour",
                "InputBox", "InStr", "InStrRev", "Int", "IsArray", "IsDate", "IsEmpty", "IsNull", "IsNumeric", "IsObject",
                "Join", "LBound", "LCase", "Left", "Len", "Log", "LTrim", "Mid", "Minute", "Month", "MsgBox", "Now",
                "Oct", "Replace", "Right", "Rnd", "Round", "RTrim", "Second", "Sin", "Split", "Sqr", "StrComp",
                "String", "StrReverse", "Time", "Timer", "Trim", "TypeName", "UBound", "UCase", "VarType", "Year"
            ],
            tokenizer: {
                root: [
                    [/\bOn\s+Error\s+(?:Resume\s+Next|GoTo)\b/, "keyword"],
                    [/\bRem\b.*$/, "comment"],
                    [/'[^\r\n]*/, "comment"],
                    [/"(?:[^"]|"")*"?/, "string"],
                    [/&H[0-9a-f]+|\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b/, "number"],
                    [/[a-z_][\w]*/, {cases: {"@keywords": "keyword", "@constants": "constant", "@builtins": "type.identifier", "@default": "identifier"}}],
                    [/[+\-*/\\^&=<>]+/, "operator"],
                    [/\s+/, "white"]
                ]
            }
        });
        monaco.languages.setLanguageConfiguration("vbscript", {
            comments: {lineComment: "'"},
            brackets: [["(", ")"]],
            autoClosingPairs: [{open: "(", close: ")"}, {open: '"', close: '"'}]
        });
    }

    private configureWorkers(): void {
        if (this.workersConfigured) {
            return;
        }

        self.MonacoEnvironment = {
            ...self.MonacoEnvironment,
            getWorker: (_moduleId: string, label: string) => label == "json"
                ? new Worker(new URL("../../../../node_modules/monaco-editor/esm/vs/language/json/json.worker.js", import.meta.url), {name: label, type: "module"})
                : new Worker(new URL("../../../../node_modules/monaco-editor/esm/vs/editor/editor.worker.js", import.meta.url), {name: label, type: "module"})
        };
        this.workersConfigured = true;
    }
}
